<template>
  <div class="author-content-manager">
    <div class="panel-header">
      <div>
        <h2><i class="fas fa-photo-video"></i> Author Content Portal</h2>
        <p class="panel-subtitle">
          Review speaker submissions, run the Course Factory, and publish CE courses.
        </p>
      </div>
      <button class="btn btn-secondary" @click="loadSubmissions" :disabled="loading">
        <i :class="loading ? 'fas fa-spinner fa-spin' : 'fas fa-sync'"></i>
        Refresh
      </button>
    </div>

    <div v-if="loading && !submissions.length" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i> Loading submissions...
    </div>

    <div v-else-if="!submissions.length" class="empty-state">
      <p>No author submissions yet. Speakers can submit from the Author Portal.</p>
    </div>

    <div v-else class="submission-table-wrap">
      <table class="submission-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Author</th>
            <th>Status</th>
            <th>Rubric</th>
            <th>Course</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in submissions" :key="item.id">
            <td>{{ item.title }}</td>
            <td>{{ item.author_name }}</td>
            <td><span :class="['status-badge', item.status.toLowerCase()]">{{ item.status }}</span></td>
            <td>{{ item.quality_rubric_score ?? '—' }}</td>
            <td>
              <router-link
                v-if="item.output_course"
                :to="`/learning/courses/${item.output_course}`"
              >
                #{{ item.output_course }}
              </router-link>
              <span v-else>—</span>
            </td>
            <td class="actions">
              <button class="btn btn-sm" @click="selectSubmission(item.id)">Review</button>
              <button
                v-if="canOps"
                class="btn btn-sm btn-secondary"
                @click="runFactory(item.id)"
              >
                Factory
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="selected" class="detail-panel">
      <h3>{{ selected.title }}</h3>
      <p>{{ selected.description }}</p>
      <p v-if="selected.author_notes"><strong>Author notes:</strong> {{ selected.author_notes }}</p>
      <p>
        <strong>Consent:</strong>
        {{ selected.consent_attested ? 'Attested' : 'Missing' }}
      </p>

      <div v-if="selected.conference_pack && Object.keys(selected.conference_pack).length" class="pack-block">
        <h4>Conference pack</h4>
        <pre>{{ JSON.stringify(selected.conference_pack, null, 2) }}</pre>
      </div>

      <div v-if="selected.factory_status?.rubric" class="rubric-block">
        <h4>Quality rubric</h4>
        <p>Score: {{ selected.factory_status.rubric.score }} — {{ selected.auto_publish_eligible ? 'Pass' : 'Needs review' }}</p>
        <ul>
          <li v-for="(check, idx) in selected.factory_status.rubric.checks" :key="idx">
            {{ check.name }}: {{ check.passed ? 'OK' : 'Fail' }} ({{ check.detail }})
          </li>
        </ul>
      </div>

      <div class="detail-actions" v-if="canOps">
        <button class="btn btn-primary" @click="evaluateRubric(selected.id)">Re-evaluate rubric</button>
        <button class="btn btn-success" @click="publishSubmission(selected.id)">Publish course</button>
        <button class="btn btn-warning" @click="requestFixes(selected.id)">Request fixes</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import lmsAPI from '@/services/lmsAPI';
import authService from '@/services/auth';

export default {
  name: 'AuthorContentManager',
  setup() {
    const submissions = ref([]);
    const selected = ref(null);
    const loading = ref(false);

    const canOps = computed(() => {
      const user = authService.getUser();
      return user && ['LMS_ADMIN', 'ORG_ADMIN', 'PLATFORM_ADMIN', 'ADMIN'].includes(user.role);
    });

    async function loadSubmissions() {
      loading.value = true;
      try {
        const res = await lmsAPI.listAuthorSubmissions();
        submissions.value = res.data.results || res.data || [];
      } finally {
        loading.value = false;
      }
    }

    async function selectSubmission(id) {
      const res = await lmsAPI.getAuthorSubmission(id);
      selected.value = res.data;
    }

    async function runFactory(id) {
      await lmsAPI.runAuthorFactory(id);
      await loadSubmissions();
      await selectSubmission(id);
    }

    async function evaluateRubric(id) {
      await lmsAPI.evaluateAuthorRubric(id);
      await selectSubmission(id);
    }

    async function publishSubmission(id) {
      await lmsAPI.publishAuthorSubmission(id);
      await loadSubmissions();
      await selectSubmission(id);
    }

    async function requestFixes(id) {
      const notes = window.prompt('Describe fixes needed for the author:');
      if (!notes) return;
      await lmsAPI.requestAuthorFixes(id, notes);
      await loadSubmissions();
      await selectSubmission(id);
    }

    onMounted(loadSubmissions);

    return {
      submissions,
      selected,
      loading,
      canOps,
      loadSubmissions,
      selectSubmission,
      runFactory,
      evaluateRubric,
      publishSubmission,
      requestFixes,
    };
  },
};
</script>

<style scoped>
.author-content-manager { padding: 1rem 0; }
.panel-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; }
.panel-subtitle { color: #64748b; margin: 0.25rem 0 0; }
.submission-table { width: 100%; border-collapse: collapse; }
.submission-table th, .submission-table td { padding: 0.75rem; border-bottom: 1px solid #e2e8f0; text-align: left; }
.status-badge { padding: 0.2rem 0.5rem; border-radius: 4px; font-size: 0.75rem; background: #e2e8f0; }
.detail-panel { margin-top: 2rem; padding: 1.25rem; background: #f8fafc; border-radius: 8px; }
.detail-actions { display: flex; gap: 0.75rem; margin-top: 1rem; flex-wrap: wrap; }
pre { max-height: 240px; overflow: auto; font-size: 0.8rem; background: #fff; padding: 0.75rem; }
.empty-state, .loading-state { padding: 2rem; text-align: center; color: #64748b; }
</style>
